/* eslint-disable camelcase */
import { AxiosResponse } from 'axios';
import { DateTime } from 'luxon';
import { getMessageError } from 'utils/helpers';
import { isAxiosError } from 'utils/isAxiosError';

import { Permissions } from 'context';
import {
  accounts,
  api,
  ErrorInvite,
  getExports,
  GetSpecialistChatUsersParams,
  InviteStatus,
  LinkReason,
  MfaConfig,
  SpecialistChatResponse,
  SpecialistDashboardUnlink,
} from 'services';
import { getPartner } from 'services/bff';

import {
  ChatStatus,
  GetBookingInvites,
  HideSpecialistInfo,
  Limit,
  LogbookRequestProps,
  NotesRequestProps,
  SpecialistDashboardCritical,
  SpecialistDashboardOverview,
  SpecialistDashboardUser,
  SpecialistResponse,
  SpecialistType,
  UsersFilterName,
} from './types';

import {
  AllowDayAndTime,
  Discharge,
  GetPatientsResponse,
  GetSessionHistoryResponse,
  GetSessionHistorySummaryResponse,
  GetSessionLinkResponse,
  SpecialistAvailability,
  SpecialistInfo,
  SpecialistProfessionalInfo,
  SpecialistSessionResponse,
} from '.';

export const getSpecialistNotes = async (
  cognitoId: string,
  cursor?: string,
  limit: Limit = '5',
) => {
  try {
    let url = `/v2/specialist/users/${cognitoId}/notes?limit=${limit}`;

    if (cursor) {
      url += `&cursor=${encodeURIComponent(cursor)}`;
    }

    const response = await api.get<void, AxiosResponse<NotesRequestProps>>(url);
    return response;
  } catch (e) {
    throw new Error(
      getMessageError(e, 'Unexpected error while retrieving user information'),
    );
  }
};

export const getSpecialistLogbook = async (
  cognitoId: string,
  cursor?: string,
  limit: Limit = '5',
) => {
  try {
    let url = `/v1/specialist/users/${cognitoId}/logbook?limit=${limit}`;

    if (cursor) {
      url += `&cursor=${encodeURIComponent(cursor)}`;
    }

    const response = await api.get<void, AxiosResponse<LogbookRequestProps>>(
      url,
    );
    return response;
  } catch (e) {
    throw new Error(
      getMessageError(
        e,
        'Unexpected error while retrieving specialist logbook',
      ),
    );
  }
};

export const getSpecialistInfo = async (): Promise<SpecialistInfo> => {
  try {
    const specialist = await getPartner();

    const { socialLink, timezone, birthdayDate, geoCode } = specialist;

    let socialUsername;

    if (socialLink) {
      const splitSocialLink = socialLink.split('/');
      socialUsername = splitSocialLink[splitSocialLink.length - 1].length
        ? splitSocialLink[splitSocialLink.length - 1]
        : splitSocialLink[splitSocialLink.length - 2];
    }

    let locale;

    if (timezone) {
      const localeSplit = timezone.split('/');
      [, locale] = localeSplit;
    }

    const [year, month, day] = birthdayDate.slice(0, 10).split('-');

    const response = {
      id: specialist.id,
      fullName: specialist.name,
      phoneNumber: specialist.phone,
      socialLink: specialist.socialLink,
      avatar: specialist.avatar,
      birthday: new Date(Number(year), Number(month) - 1, Number(day)),
      email: specialist.email,
      gender: specialist.gender,
      videoId: '',
      socialUsername: socialUsername || '',
      locale: locale || '',
      geoCode,
    } as unknown as SpecialistInfo;

    return response;
  } catch {
    throw new Error(
      'Unexpected error while retrieving care provider information',
    );
  }
};

export const getSpecialistType = (type: string) => {
  if (type === 'CP') {
    return SpecialistType.CP;
  }

  if (type === 'TC') {
    return SpecialistType.TC;
  }

  return undefined;
};

export const getSpecialistProfessionalInfo =
  async (): Promise<SpecialistProfessionalInfo> => {
    try {
      const specialist = await getPartner();

      const specialities = specialist.specialities.map(({ titleKey }) => ({
        titleKey,
      }));

      const response: SpecialistProfessionalInfo = {
        type: getSpecialistType(specialist?.partnerTypeId),
        specialities,
        professionalNumber:
          specialist?.professionalInfo?.professionalNumber || '',
        aboutYou: specialist?.aboutYou || '',
        epsiNumber: '',
        education: specialist?.education.academicGraduation || '',
      };

      return response;
    } catch {
      throw new Error(
        'Unexpected error while retrieving care provider professional information',
      );
    }
  };

export const getSpecialists = async (
  exludeMe?: boolean,
  type?: SpecialistType,
  preferredLinkReason?: LinkReason,
  userCognitoID?: string,
) => {
  try {
    const queryParams = new URLSearchParams();

    if (type) {
      queryParams.append('type', type);
    }
    if (exludeMe) {
      queryParams.append('exclude', 'me');
    }
    if (preferredLinkReason) {
      queryParams.append('preferred_link_reasons', preferredLinkReason);
    }
    if (userCognitoID) {
      queryParams.append('user_cognito_id', userCognitoID);
    }

    const stringParams = `${
      exludeMe || type || preferredLinkReason ? '?' : ''
    }${queryParams}`;

    const url = `/v1/specialist/available${stringParams}`;

    const data = await api.get<void, AxiosResponse<SpecialistResponse[]>>(url);
    return data;
  } catch (e) {
    throw new Error(
      getMessageError(e, 'Unexpected error while retrieving specialists'),
    );
  }
};

export const getPatients = async (
  limit?: number,
  filterByAttention?: boolean,
  cursor?: string,
  patientName?: string,
) => {
  try {
    let url = `v2/specialist/users?`;

    if (limit) {
      url += `limit=${limit}`;
    }

    if (filterByAttention) {
      url += `&patient_attention=true`;
    }

    if (cursor) {
      url += `&cursor=${encodeURIComponent(cursor)}`;
    }

    if (patientName) {
      url += `&patient_name=${patientName}`;
    }

    const { data } = await api.get<void, AxiosResponse<GetPatientsResponse>>(
      url,
    );
    return data;
  } catch (e) {
    throw new Error(
      getMessageError(e, 'Unexpected error while retrieving patients'),
    );
  }
};

export const setPatientAttention = async (
  cognitoId: string,
  attentionValue: boolean,
) => {
  try {
    const response = await api.patch(
      `/v1/specialist/users/${cognitoId}/patient-attention`,
      {
        patient_attention: attentionValue,
      },
    );

    return response;
  } catch (e) {
    throw new Error(
      getMessageError(e, 'Unexpected error setting patient attention value'),
    );
  }
};

export const getSessionHistory = async (
  year: number,
  month = 0,
  timezone: string,
  cursor?: string,
  column?: string,
  direction?: string,
  limit = 10,
): Promise<GetSessionHistoryResponse> => {
  try {
    let endpointUrl = `/v1/specialist/sessions/${year}?timezone=${encodeURIComponent(
      timezone,
    )}&month=${month}&limit=${limit}`;

    if (cursor) {
      endpointUrl += `&cursor=${encodeURIComponent(cursor)}`;
    }

    if (column && direction) {
      endpointUrl += `&field=${column}&direction=${direction}`;
    }

    const { data } = await api.get<
      void,
      AxiosResponse<GetSessionHistoryResponse>
    >(endpointUrl);

    return data;
  } catch (e) {
    throw new Error(
      getMessageError(e, 'Unexpected error while retrieving session history'),
    );
  }
};

export const getSessionHistorySummary = async (
  year: number,
  month = 0,
  timezone: string,
) => {
  try {
    const { data } = await api.get<
      void,
      AxiosResponse<GetSessionHistorySummaryResponse>
    >(
      `/v1/specialist/sessions/${year}/summary?month=${month}&timezone=${encodeURIComponent(
        timezone,
      )}`,
    );

    return data;
  } catch (e) {
    throw new Error(
      getMessageError(
        e,
        'Unexpected error while retrieving session history summary',
      ),
    );
  }
};

export const getPermissions = async () => {
  try {
    const { data } = await api.get<void, AxiosResponse<Permissions>>(
      '/v1/specialist/permission',
    );
    return data;
  } catch (e) {
    throw new Error(getMessageError(e, 'ERROR.PERMISSIONS'));
  }
};

export const updateEndUserDischarge = async (
  cognitoId: string,
  discharge: Discharge,
) => {
  try {
    await api.patch<void, AxiosResponse<Discharge>>(
      `/v1/specialist/discharges/users/${cognitoId}`,
      { ...discharge },
    );
  } catch (e) {
    throw new Error(
      getMessageError(e, 'Unexpected error while discharging the end user'),
    );
  }
};

export const getSpecialistAvailability = async () => {
  try {
    const { data } = await api.get<void, AxiosResponse<SpecialistAvailability>>(
      '/v1/timekit/resources/availability',
    );
    return data;
  } catch (e) {
    throw new Error(
      getMessageError(e, 'Unexpected error while fetching CP availability'),
    );
  }
};

export const getSpecialistSessions = async (
  timezone: string,
  startAt?: string,
  endAt?: string,
  status = 'confirmed',
): Promise<SpecialistSessionResponse> => {
  try {
    let url = `/v1/specialist/sessions?timezone=${encodeURIComponent(
      timezone,
    )}&status=${status}`;

    if (startAt) {
      url += `&start_at=${startAt}`;
    }

    if (endAt) {
      url += `&end_at=${endAt}`;
    }

    const { data } = await api.get<SpecialistSessionResponse>(url);
    return data;
  } catch (e) {
    throw new Error(
      getMessageError(e, 'Unexpected error while fetching CP sessions'),
    );
  }
};

export const getSessionLink = async (endUserCognitoId: string) => {
  try {
    const { data } = await api.get<GetSessionLinkResponse>(
      `/v1/specialist/join-session?scheduled_user=${endUserCognitoId}`,
    );

    return data;
  } catch (e) {
    throw new Error(
      getMessageError(e, 'Unexpected error while getting session link'),
    );
  }
};

export const deleteBooking = async (bookingId: string) => {
  try {
    await api.delete<void, AxiosResponse<unknown>>(
      `/v1/specialist/bookings/${bookingId}`,
    );
  } catch (e) {
    throw new Error(
      getMessageError(e, 'Unexpected error while deleting session'),
    );
  }
};
export const getBookingsInvites = async (
  status: InviteStatus | null,
  cursor?: string,
) => {
  try {
    const { data } = await api.get<GetBookingInvites>(`/v1/bookings/invites`, {
      params: {
        status,
        cursor,
        limit: 10,
      },
    });
    return data;
  } catch (e) {
    throw new Error(
      getMessageError(e, 'Unexpected error while get bookings invites'),
    );
  }
};

export const updateRecurrentAvailability = async (
  availability_constraints: AllowDayAndTime[],
) => {
  try {
    await api.put<void, AxiosResponse<unknown>>('/v1/specialist/availability', {
      availability_constraints,
    });
  } catch (e) {
    throw new Error(
      getMessageError(
        e,
        'Unexpected error while updating specialist availability',
      ),
    );
  }
};

export const getSpecialistDashboardCritical = async () => {
  try {
    const endpointUrl = `/v1/specialist/users/critical`;
    const { data } = await api.get<
      void,
      AxiosResponse<SpecialistDashboardCritical>
    >(endpointUrl);

    return data;
  } catch {
    throw new Error(
      'Unexpected error while retrieving CP specialist dashboard overview',
    );
  }
};

export const getSpecialistDashboardUnlink = async () => {
  try {
    const endpointUrl = `/v1/specialist/users/unlinks`;
    const { data } = await api.get<
      void,
      AxiosResponse<SpecialistDashboardUnlink>
    >(endpointUrl);

    return data;
  } catch (error) {
    throw new Error(
      'Unexpected error while retrieving CP specialist dashboard overview',
    );
  }
};

export const getSpecialistDashboardOverview = async () => {
  try {
    const endpointUrl = `/v2/specialist/dashboard/summary`;
    const { data } = await api.get<
      void,
      AxiosResponse<SpecialistDashboardOverview>
    >(endpointUrl);

    return data;
  } catch {
    throw new Error(
      'Unexpected error while retrieving specialist dashboard overview',
    );
  }
};

export const getSpecialistDashboardUsers = async (type?: UsersFilterName) => {
  try {
    type Params = {
      type?: UsersFilterName;
    };
    const params: Params = {};

    if (type) {
      params.type = type;
    }

    const endpointUrl = `/v2/specialist/dashboard/users`;
    const { data } = await api.get<
      void,
      AxiosResponse<SpecialistDashboardUser[]>
    >(endpointUrl, { params });

    return data;
  } catch {
    throw new Error(
      'Unexpected error while retrieving specialist dashboard users',
    );
  }
};

export const getSpecialistChatUsers = async ({
  userName,
  hasChatMessage,
  hasPatientInteraction,
  hasUnreadMessage,
  hasScheduledMessage,
  nextCursor,
  cognitoId,
}: GetSpecialistChatUsersParams) => {
  interface Params {
    name?: string;
    has_chat_message?: boolean;
    has_enduser_interaction?: boolean;
    only_unread_messages?: boolean;
    scheduled_messages?: 'all_scheduled_messages';
    cursor?: string;
    user_cognito_id?: string;
  }

  try {
    const params: Params = {};

    if (nextCursor) {
      params.cursor = nextCursor;
    }

    if (typeof hasChatMessage === 'boolean') {
      params.has_chat_message = hasChatMessage;
    }

    if (typeof hasPatientInteraction === 'boolean') {
      params.has_enduser_interaction = hasPatientInteraction;
    }

    if (typeof hasUnreadMessage === 'boolean') {
      params.only_unread_messages = hasUnreadMessage;
    }

    if (typeof hasScheduledMessage === 'boolean') {
      params.scheduled_messages = 'all_scheduled_messages';
    }

    if (userName) {
      params.name = userName;
    }

    if (cognitoId) {
      params.user_cognito_id = cognitoId;
    }

    const endpointUrl = '/v2/specialist/chats/users';
    const { data } = await api.get<SpecialistChatResponse>(endpointUrl, {
      params,
    });

    return {
      data: data.results,
      metadata: data.metadata,
    };
  } catch {
    throw new Error('Unexpected error while retrieving specialist chat users');
  }
};

export const pingChat = async (conversationId: string) => {
  try {
    await api.patch(`/v1/specialist/chats/${conversationId}/status`);
  } catch (e) {
    throw new Error(
      getMessageError(e, 'Unexpected error while updating chat status'),
    );
  }
};

export const getChatStatus = async () => {
  try {
    const { data } = await api.get<ChatStatus[]>('/v1/specialist/chats/status');

    return data;
  } catch (e) {
    throw new Error(
      getMessageError(e, 'Unexpected error while getting chat status'),
    );
  }
};

export const getHideSpecialistInfoToNewUsers = async () => {
  try {
    const { data } = await api.get<HideSpecialistInfo>('/v1/specialist/me');

    const response = {
      hideCpProfileToNewUsers: data.can_receive_new_users,
      canToggleToReceiveNewUsers: data.can_toggle_to_receive_new_users,
    };

    return response;
  } catch (e) {
    throw new Error(
      getMessageError(e, 'Unexpected error while getting HideCP information.'),
    );
  }
};

export const updateCanReceiveNewUsers = async (
  can_receive_new_users: boolean,
) => {
  try {
    await api.patch('/v1/specialist/', {
      can_receive_new_users,
    });
  } catch (e) {
    throw new Error(
      getMessageError(
        e,
        'Unexpected error while updating canReceiveNewUsers value.',
      ),
    );
  }
};

export const forwardUserToTC = async (
  userCognitoId: string,
  specialistCognitoId: string,
) => {
  try {
    const url = `/v1/specialist/users/${userCognitoId}/forward/${specialistCognitoId}`;
    await api.post<void, AxiosResponse<void>>(url);
  } catch (e) {
    throw new Error(
      getMessageError(e, 'Unexpected error while forwarding user to TC'),
    );
  }
};

export const resetTemporaryPassword = async (email: string) => {
  const { arkhamApiToken: token } = await getExports();

  try {
    await api.post<void, AxiosResponse>(
      'v1/specialist/reset-temporary-password',
      {
        email,
      },
      {
        headers: {
          authorization: `Bearer ${token}`,
        },
      },
    );
    return true;
  } catch (e) {
    throw new Error(
      getMessageError(e, 'Unexpected error while reseting temporary password'),
    );
  }
};

export const getSpecialistMfaConfig = async () => {
  try {
    const { data } = await accounts.get<MfaConfig>('v1/mfa/config');
    return data;
  } catch (e) {
    if (isAxiosError(e) && e.response?.status === 404) {
      return {} as MfaConfig;
    }

    throw new Error(
      getMessageError(
        e,
        'Unexpected error while getting specialist mfa config',
      ),
    );
  }
};

export const createInviteSession = async ({
  userId,
  date,
  sessionId,
}: {
  userId: string;
  date: DateTime;
  sessionId?: string;
}): Promise<string> => {
  try {
    const response = await api.post<ErrorInvite | string>(
      'v1/bookings/invites',
      {
        user_id: userId,
        slot_date: date,
        session_id: sessionId,
      },
      {
        validateStatus: status =>
          (status >= 200 && status < 300) || status === 400,
      },
    );

    if (response.status === 400) {
      if (
        response.data === 'PAST_DATE' ||
        response.data === 'LIMIT_INVITE_DATE'
      ) {
        return response.data;
      }

      throw new Error('Error creating session invite');
    }

    return response.data;
  } catch (e) {
    throw new Error(getMessageError(e, 'Error creating session invite'));
  }
};
